// Falls back to "/api" when VITE_API_URL isn't set so a missing .env
// can't silently emit "undefined" into request URLs. The Vite dev server
// proxies /api → backend (vite.config.js); prod backend routes are all
// /api/... so the same prefix works in both environments.
const prefix = import.meta.env.VITE_API_URL ?? "/api";
import { useStore } from "./store";
import { navigate } from "wouter/use-browser-location";
import { notifications } from "@mantine/notifications";

const jsonHeaders = {
  "content-type": "application/json",
  accept: "application/json",
};

function getAuthHeaders() {
  const authToken = useStore.getState().authToken;
  if (authToken) return { authorization: `Bearer ${authToken}` };
  return {};
}

async function throwOnError(response, options = {}) {
  if (response.ok) return;
  if (response.status === 502) {
    notifications.show({
      color: "red",
      autoClose: 4000,
      title: "System Unavailable",
      message:
        "Seems our servers are having a bad day. Sorry about that! Please try again in a few minutes.",
    });
    throw new Error("Gateway Timeout");
  }
  if (response.status === 401) {
    // Use the store's logout() — clears authToken, profile and pluginData
    // (all persisted), so NavBar/etc. don't show stale state from the
    // prior session after a 401-triggered sign-out.
    useStore.getState().logout();
    notifications.show({
      color: "red",
      autoClose: 4000,
      title: "You are logged out",
      message: "Please login again",
    });
    navigate("/", { replace: true });
  }

  if (response.status === 429 && !options.preferServerDetail)
    throw new Error(
      "Hold your horses! You've reached the rate limit for now. Give our GPUs some time to cool off and check back later!",
    );

  if (response.status === 403) {
    // Plan-gated endpoint (upload/generate/video) hit without a paid plan.
    notifications.show({
      color: "red",
      autoClose: 5000,
      title: "Paid plan required",
      message: "That feature needs an active plan. Sending you to the plans page…",
    });
    navigate("/payment/plans", { replace: true });
    throw new Error("Paid plan required");
  }

  let json = {};
  let text = "";
  try {
    text = await response.text();
    json = JSON.parse(text);
  } catch {}

  const message = `An error has occured: ${response.status}: ${response.statusText}}`;
  throw new Error(json?.detail ?? json?.title ?? text ?? message);
}

export async function jsonApiRequest(method, uri, values, options = {}) {
  let authHeader = getAuthHeaders();
  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };

  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
    signal: options.signal,
  });

  await throwOnError(response, options);
  if (response.status === 204) return {};

  const json = await response.json();
  return json;
}

export async function jsonPost(uri, values, options) {
  return await jsonApiRequest("POST", uri, values, options);
}

export async function jsonGet(uri, options) {
  return await jsonApiRequest("GET", uri, null, options);
}

export async function jsonPut(uri, values, options) {
  return await jsonApiRequest("PUT", uri, values, options);
}

export async function jsonDelete(uri, options) {
  return await jsonApiRequest("DELETE", uri, null, options);
}

export async function pollJob(jobId, options) {
  if (!jobId) throw "missing required parameter jobId!";
  return await jsonGet(`jobs/${jobId}`, options);
}

export async function getJobResults(jobId, options) {
  if (!jobId) throw "missing required parameter jobId!";
  return await jsonGet(`jobs/${jobId}/results`, options);
}

export async function getActiveJobs(options) {
  return await jsonGet("jobs", options);
}

export async function deleteGalleryImage(imageId) {
  return await jsonDelete(`images/${imageId}`);
}

export async function deleteAllGalleryImages() {
  return await jsonDelete(`images/all`);
}

export async function deleteUserAccount() {
  return await jsonDelete(`auth/profile`);
}

export async function fetchPublicBundle(bundleId) {
  return await jsonGet(`public/bundles/${bundleId}`);
}

export async function fetchBundles(page = 1, pageSize = 50) {
  return await jsonGet(`bundles?page=${page}&pageSize=${pageSize}`);
}

export async function fetchBundle(bundleId) {
  return await jsonGet(`bundles/${bundleId}`);
}

export async function createBundle({ title, description, coverImageId, isPublic } = {}) {
  return await jsonPost("bundles", {
    title,
    description: description ?? null,
    coverImageId: coverImageId ?? null,
    isPublic: !!isPublic,
  });
}

export async function updateBundle(bundleId, { title, description, coverImageId, isPublic } = {}) {
  return await jsonPut(`bundles/${bundleId}`, {
    title,
    description: description ?? null,
    coverImageId: coverImageId ?? null,
    isPublic: !!isPublic,
  });
}

export async function deleteBundle(bundleId) {
  return await jsonDelete(`bundles/${bundleId}`);
}

export async function addImagesToBundle(bundleId, imageIds) {
  return await jsonPost(`bundles/${bundleId}/images`, { imageIds });
}

export async function removeImagesFromBundle(bundleId, imageIds) {
  const qs = imageIds.map((id) => `imageIds=${encodeURIComponent(id)}`).join("&");
  return await jsonDelete(`bundles/${bundleId}/images?${qs}`);
}

export async function deleteImages(imageIds) {
  // This only works for about 27 images at a time becaus of max path length
  let imgIds = imageIds
    ? "?".concat(imageIds.map((p) => `imageIds=${p}&`))
    : "";
  return await jsonDelete("images" + imgIds);
}

export async function fetchGalleryModerator(
  page,
  pageSize = 27,
  userFilter = null,
  tagsFilter = null,
) {
  let tags = tagsFilter ? "".concat(tagsFilter.map((p) => `&tags=${p}`)) : "";
  let user = userFilter ? `&ownerType=${userFilter}` : "";
  return await jsonGet(
    `mod/images?page=${page}&pageSize=${pageSize}${user}${tags}`,
  );
}

export async function getImage(imageId) {
  return await jsonGet(`images/${imageId}`);
}

export async function fetchGallery(
  page,
  pageSize = 27,
  styleId = null,
  userFilter = null,
  tagsFilter = null,
) {
  let tags = tagsFilter ? "".concat(tagsFilter.map((p) => `&tags=${p}`)) : "";
  let user = userFilter ? `&ownerType=${userFilter}` : "";
  let style = styleId ? `&styleId=${styleId}` : "";
  // let skip = (page - 1) * pageSize;
  return await jsonGet(
    `images?page=${page}&pageSize=${pageSize}${user}${tags}${style}`,
  );
}

export async function fetchGalleryPublic(
  page,
  pageSize = 27,
  styleId = null,
  userFilter = null,
  tagsFilter = null,
) {
  let tags = tagsFilter ? "".concat(tagsFilter.map((p) => `&tags=${p}`)) : "";
  let user = userFilter ? `&ownerType=${userFilter}` : "";
  let style = styleId ? `&styleId=${styleId}` : "";
  let skip = (page - 1) * pageSize;
  return await jsonGet(
    `public/images?skip=${skip}&limit=${pageSize}${user}${tags}${style}`,
  );
}

export async function getCustomModels(source, baseModel = "") {
  let url = "custom-models?source=" + source;
  if (baseModel) url += "&baseModel=" + baseModel;
  return await jsonGet(url);
}

export async function createTrainingSet(imageIds) {
  return await jsonPost("training-datasets", { imageIds });
}

export async function updateDataset(id, dto) {
  return await jsonPut(`training-datasets/${id}`, dto);
}

export async function startTraining(id, baseModel = "") {
  let url = `training-datasets/${id}/train`;
  if (baseModel) url += `?baseModel=${baseModel}`;
  return await jsonPost(url);
}

export async function getTrainingDataset(id) {
  return await jsonGet("training-datasets/" + id);
}

export async function deleteTrainingDataset(id) {
  return await jsonDelete(`training-datasets/${id}`);
}

export async function getTrainingDatasets() {
  return await jsonGet("training-datasets");
}

export async function createGenerateJobAdmin(dto) {
  return await jsonPost("admin/jobs/free-form", dto);
}

export const getAdminJobs = async (page, pageSize, userFilter, statusFilter) =>
  await jsonGet(
    `admin/jobs?page=${page}&pageSize=${pageSize}&userFilter=${userFilter}&statusFilter=${statusFilter}`,
  );

export const getAdminJobDetails = async (id) =>
  await jsonGet(`admin/jobs/${id}`);
export const requeueJob = async (id) =>
  await jsonPost(`admin/jobs/${id}/retry`);
export const requeueJobHard = async (id) =>
  await jsonPost(`admin/jobs/${id}/retry-hard`);

// export async function getJobsAdmin() {
//   return await jsonGet("admin/jobs");
// }

// export async function getJobDetailsAdmin(jobId) {
//   return await jsonGet(`admin/jobs/${jobId}`);
// }

export async function getJobsForResultImage(imageId) {
  return await jsonGet(`mod/images/${imageId}/jobs`);
}

export async function getUsersAdmin(
  page,
  pageSize,
  search = null,
  role = null,
  plan = null,
) {
  return await jsonGet(
    `admin/users?page=${page}&pageSize=${pageSize}&search=${search}&role=${role}&plan=${plan}`,
  );
}

export async function getUserAdmin(id) {
  return await jsonGet(`admin/users/${id}`);
}

export async function adminSetUserPlan(dto) {
  return await jsonPut("admin/users/plan", dto);
}

export async function adminAdjustUserCredits(dto) {
  return await jsonPost("admin/users/credits", dto);
}

export async function adminImpersonateUser(dto) {
  return await jsonPost("auth/email/impersonate", dto);
}

export async function adminDeleteUser(id) {
  return await jsonDelete(`admin/users/${id}`);
}

export async function adminGetPlugins() {
  return await jsonGet("admin/plugins");
}

export async function adminGetPluginOptions(pluginId) {
  return await jsonGet(`admin/plugins/${pluginId}/options`);
}

export async function adminCreatePluginOptionValue(pluginId, optionId, value) {
  return await jsonPost(
    `admin/plugins/${pluginId}/options/${optionId}/values`,
    value,
  );
}

export async function adminUpdatePluginOptionValue(
  pluginId,
  optionId,
  valueId,
  value,
) {
  return await jsonPut(
    `admin/plugins/${pluginId}/options/${optionId}/values/${valueId}`,
    value,
  );
}

export async function adminDeletePluginOptionValue(
  pluginId,
  optionId,
  valueId,
) {
  return await jsonDelete(
    `admin/plugins/${pluginId}/options/${optionId}/values/${valueId}`,
  );
}

export async function getStyles() {
  return await jsonGet("styles");
}

export async function getStyle(id) {
  return await jsonGet(`styles/${id}`);
}

export async function createStyle(style) {
  return await jsonPost(`styles`, style);
}

export async function updateStyle(style) {
  return await jsonPut(`styles/${style.id}`, style);
}

export async function getGenerationOptions(
  source,
  mediaClass = "image",
  baseModel = "",
) {
  var url = `generation/options?source=${source}&mediaClass=${mediaClass}`;
  if (baseModel) url += `&baseModel=${baseModel}`;
  return await jsonGet(url);
}

export async function deleteImage(imageId) {
  return await jsonDelete(`mod/images/${imageId}`);
}

export async function setImageApproval(logicalImageId, approval) {
  return await jsonPut(`mod/images/${logicalImageId}/approval`, { approval });
}

/**
 * Records a satisfaction vote ("up" | "down") for one finished result image.
 */
export async function submitFeedback(logicalImageId, score) {
  return await jsonPost("feedback", { logicalImageId, score });
}

/**
 * Admin: satisfaction rollup per subject bucket. groupBy: plugin|operation|model.
 */
export async function getAdminFeedbackOverview(groupBy = "plugin", days = 7, minVotes = 1) {
  return await jsonGet(
    `admin/feedback/overview?groupBy=${groupBy}&days=${days}&minVotes=${minVotes}`
  );
}

/**
 * Admin: individual votes in one overview bucket.
 */
export async function getAdminFeedbackVotes(
  groupBy,
  subject,
  days = 7,
  page = 1,
  pageSize = 40
) {
  const q = new URLSearchParams({
    groupBy,
    subject,
    days: String(days),
    page: String(page),
    pageSize: String(pageSize),
  });
  return await jsonGet(`admin/feedback/votes?${q.toString()}`);
}

export async function setImageMeta(logicalImageId, dto) {
  return await jsonPut(`mod/images/${logicalImageId}/meta`, dto);
}

export async function recreateImage(imageSha) {
  return await jsonPost(`jobs/images/${imageSha}/job`);
  // var response = await fetch(`${prefix}/jobs/images/${imageSha}/job`, {
  //   method: "POST",
  //   headers: {
  //     "X-Client-Job-Id": clientJobId,
  //     "X-Session-Id": sessionId,
  //   }
  // });

  // var json = await response.json();
  // return json;
}

export async function adminDeleteUserCredit(id) {
  return await jsonDelete(`admin/users/credits/${id}`);
}

export async function getProducts() {
  return await jsonGet(`payment/stripe/products`);
}
export async function checkoutSubscription(dto) {
  return await jsonPost(`payment/stripe/checkout-plan`, dto);
}
export async function checkoutCredits(dto) {
  return await jsonPost("payment/stripe/checkout-credits", dto);
}
export async function customerPortal() {
  // mixpanel.track("Customer Portal");
  return await jsonPost("payment/stripe/portal");
}
export async function createSamplingRunsAdmin(dto) {
  return await jsonPost("admin/sampling", dto);
}

export async function getSamplingRunsAdmin() {
  return await jsonGet(`admin/sampling`);
}

export async function getSamplingRunGalleryAdmin(id) {
  return await jsonGet(`admin/sampling/${id}/images`);
}

export async function getPluginPublicKey() {
  return await jsonGet("plugins/public-key");
}

export async function getPlugins() {
  return await jsonGet("plugins");
}

export async function deletePlugin(pluginId) {
  return await jsonDelete("plugins/" + pluginId);
}

export async function uploadPlugin(file) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/plugins`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });
  await throwOnError(response);
  var json = await response.json();
  return json;
}

export async function installPluginFromUrl(url) {
  return await jsonPost("plugins/install-from-url", { url });
}

export class ImageAlreadyExistsError extends Error {
  constructor(images) {
    super("Image already exists in your gallery");
    this.name = "ImageAlreadyExistsError";
    this.images = images;
  }
}

export async function uploadImage(file) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/images`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });

  if (response.status === 409) {
    let images = [];
    try {
      const body = await response.json();
      if (Array.isArray(body)) images = body;
    } catch {}
    throw new ImageAlreadyExistsError(images);
  }

  await throwOnError(response);
  var json = await response.json();
  return json;
}

export async function createJob(dto) {
  return await jsonPost("jobs", dto);
}

export async function generateImage(dto) {
  return await jsonPost("generation", dto);
}

/**
 * Manifest v2 plugin run. `inputValues` is a flat map of declared input
 * name → user-supplied value (Image inputs use the LogicalImage id as the
 * value). `presets` maps an exclusive-gate input name → chosen invocation
 * template id (only sent when non-empty). `typedPrompt` is the raw free-text
 * prompt the customer authored (never the chip-composed subject); it is
 * stamped onto outputs so the editor shows a prompt only when genuine.
 * Returns { bundleId, jobIds }.
 */
export async function runPlugin(pluginId, inputValues, overrides = {}, presets = {}, typedPrompt) {
  const body = { inputValues, ...overrides };
  const presetKeys = Object.keys(presets);
  if (presetKeys.length > 0) body.presetSelections = presets;
  if (typedPrompt && typedPrompt.trim()) body.typedPrompt = typedPrompt;
  return await jsonPost(`plugins/${pluginId}/run`, body);
}

/**
 * Stages a plugin preview image on our CDN (admin-only). Returns { url, blobName }
 * — paste `url` into the manifest's preview_image_url. Content-addressed, so
 * re-uploading identical bytes is a cheap no-op.
 */
export async function uploadPreviewImage(file) {
  const response = await fetch(`${prefix}/admin/preview-images`, {
    method: "POST",
    body: file,
    headers: {
      ...getAuthHeaders(),
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });
  await throwOnError(response);
  return await response.json();
}

export async function generateVideo(dto) {
  return await jsonPost("video-generation", dto);
}

export async function getVideoModels() {
  return await jsonGet("video-generation/models");
}

export async function createNanoBanana(dto) {
  return await jsonPost("generation/nanobanana", dto);
}

/**
 * Unmoderated single-image edit on the unified FLUX.2 dev engine
 * (fal-ai/flux-2/lora/edit, generation/img2img). Unlike nano-banana this
 * engine has no upstream safety checker, so sources that stricter models
 * refuse are still accepted here.
 */
export async function createFluxImg2Img(dto) {
  return await jsonPost("generation/img2img", dto);
}

export async function expandPrompt(sourceId, prompt) {
  return await jsonPost("generation/expand-prompt", { sourceId, prompt });
}

export async function getMyProfile() {
  return await jsonGet("auth/profile");
}

export async function getMyCredits() {
  return await jsonGet("auth/credits");
}

export async function getTurnstileSiteKey() {
  return await jsonGet("auth/email/turnstile");
}

export async function requestLogin({ email, turnstileToken }) {
  return await jsonPost(
    "auth/email",
    { email, turnstileToken },
    { preferServerDetail: true },
  );
}

export async function loginWithToken(token) {
  return await jsonPost("auth/email/token", { token });
}

// export async function register(provider) {
//   // var response = await fetch(`${prefix}/auth/signin?provider=${provider}`, {
//   //   method: "POST"
//   // });
//   // console.debug("Received: ", response.headers.location);
//   // console.debug("Received: ", response.url);
//   // console.debug("Received: ", response);
//   // if(response.redirected) {
//     const width = 450;
//     const height = 730;
//     const left = window.screen.width / 2 - width / 2;
//     const top = window.screen.height / 2 - height / 2;

//     const popupWindowURL = new URL("http://localhost:5173/example");
//     const code = popupWindowURL.searchParams.get('code');
//     const state = popupWindowURL.searchParams.get('state');
//     window.open(popupWindowURL, "_blank",  'menubar=no,location=no,resizable=no,scrollbars=no,status=no, width=' +
//         width +
//         ', height=' +
//         top +
//         ', left=' +
//         left);

//   // }
// }

export async function login(dto) {
  var response = await fetch(`${prefix}/login`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(dto),
  });

  if (!response.ok) {
    const message = `An error has occured: ${response.status}`;
    throw new Error(message);
  }

  var json = await response.json();
  return json;
}
